import type { ToggleName } from '@tilelist/domain/experiments/Toggles.ts';

declare global {
    namespace App {
        interface Onex {
            DUMMY: 'groupA' | 'groupB';
            DUMMY_N_GROUP: 'groupA' | 'groupB' | 'groupC';
            E3891: 'E3891A' | 'E3891B';
            E3695: 'E3695A' | 'E3695B';
            E3772: 'StatusQuo' | 'Testgroup';
            E3822: 'E3822A' | 'E3822B';
            E3804: 'E3804A' | 'E3804B';
            E3823: 'E3823A' | 'E3823B' | 'E3823C' | 'E3823D' | 'E3823E';
            E3851: 'E3851A' | 'E3851B';
            E3867: 'E3867A' | 'E3867B';
            E3763: 'StatusQuo' | 'Positions';
        }
    }
}

type OnexExperimentId = keyof App.Onex;
type OnexGroup<Id extends OnexExperimentId> = App.Onex[Id];
export type OnexAssignments = Partial<{ [K in OnexExperimentId]: App.Onex[K] }>;

/**
 *
 */
export const EXPERIMENT_ONEX_IDS = {
    DUMMY: 'DUMMY',
    DUMMY_N_GROUP: 'DUMMY_N_GROUP',
    ADDITIONAL_IMAGES: 'E3891',
    COMPARISON_ADDITIONAL_CATEGORIES: 'E3695',
    TEASER_VARIATION_BOOST: 'E3772',
    GALLERY_LANDSCAPE_TWO_COLUMN: 'E3822',
    BRAND_TILE: 'E3804',
    UVP_DISPLAY: 'E3823',
    CUTOUT_IMAGE: 'E3851',
    ARRANGEMENT_LIST_ELEMENTS_ON_TOP: 'E3763',
    CLEAN_TILE_V2: 'E3867',
} as const satisfies Record<string, OnexExperimentId>;

export type ExperimentId = keyof typeof EXPERIMENT_ONEX_IDS;

export type ExperimentOnexId = (typeof EXPERIMENT_ONEX_IDS)[ExperimentId];

export type ExperimentGroups = {
    [K in ExperimentId]: OnexGroup<(typeof EXPERIMENT_ONEX_IDS)[K]>;
};

/**
 *
 */
export interface ExperimentConfig<E extends keyof ExperimentGroups> {
    /**
 *
 */
    onexExperimentId: (typeof EXPERIMENT_ONEX_IDS)[E];

    /**
 *
 *
 *
 */
    experimentToggle: ToggleName;

    /**
 *
 *
 *
 *
 */
    featureToggle: ToggleName;

    /**
 *
 *
 */
    groups: ReadonlyArray<ExperimentGroups[E]>;

    /**
 *
 *
 */
    defaultGroup: ExperimentGroups[E];

    /**
 *
 *
 */
    featureOnGroup: ExperimentGroups[E];
}

export type ExperimentConfigs = {
    [K in keyof ExperimentGroups]: ExperimentConfig<K>;
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const EXPERIMENTS = {
    DUMMY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY,
        experimentToggle: 'EXPERIMENT_DUMMY_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_USE_GROUP_A',
        groups: ['groupA', 'groupB'],
        defaultGroup: 'groupB',
        featureOnGroup: 'groupA',
    },
    DUMMY_N_GROUP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY_N_GROUP,
        experimentToggle: 'EXPERIMENT_DUMMY_N_GROUP_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_N_GROUP_USE_GROUP_B',
        groups: ['groupA', 'groupB', 'groupC'],
        defaultGroup: 'groupA',
        featureOnGroup: 'groupB',
    },
    ADDITIONAL_IMAGES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ADDITIONAL_IMAGES,
        experimentToggle: 'REPTILE_1522_ADDITIONAL_IMAGES_EXPERIMENT',
        featureToggle: 'REPTILE_1522_ADDITIONAL_IMAGES',
        groups: ['E3891A', 'E3891B'],
        defaultGroup: 'E3891A',
        featureOnGroup: 'E3891B',
    },
    COMPARISON_ADDITIONAL_CATEGORIES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.COMPARISON_ADDITIONAL_CATEGORIES,
        experimentToggle: 'REPTILE_1529_COMPARISON_ADDITIONAL_CATEGORIES_EXPERIMENT',
        featureToggle: 'REPTILE_1529_COMPARISON_ADDITIONAL_CATEGORIES',
        groups: ['E3695A', 'E3695B'],
        defaultGroup: 'E3695A',
        featureOnGroup: 'E3695B',
    },
    TEASER_VARIATION_BOOST: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.TEASER_VARIATION_BOOST,
        experimentToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS_EXPERIMENT',
        featureToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS',
        groups: ['StatusQuo', 'Testgroup'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Testgroup',
    },
    GALLERY_LANDSCAPE_TWO_COLUMN: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.GALLERY_LANDSCAPE_TWO_COLUMN,
        experimentToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE_EXPERIMENT',
        featureToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE',
        groups: ['E3822A', 'E3822B'],
        defaultGroup: 'E3822A',
        featureOnGroup: 'E3822B',
    },
    BRAND_TILE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.BRAND_TILE,
        experimentToggle: 'REPTILE_1537_BRAND_TILE_EXPERIMENT',
        featureToggle: 'REPTILE_1537_BRAND_TILE',
        groups: ['E3804A', 'E3804B'],
        defaultGroup: 'E3804A',
        featureOnGroup: 'E3804B',
    },
    UVP_DISPLAY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.UVP_DISPLAY,
        experimentToggle: 'REPTILE_1608_UVP_DISPLAY_EXPERIMENT',
        featureToggle: 'REPTILE_1608_UVP_DISPLAY',
        groups: ['E3823A', 'E3823B', 'E3823C', 'E3823D', 'E3823E'],
        defaultGroup: 'E3823A',
        featureOnGroup: 'E3823A',
    },
    CUTOUT_IMAGE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.CUTOUT_IMAGE,
        experimentToggle: 'REPTILE_1610_CUTOUT_IMAGE_EXPERIMENT',
        featureToggle: 'REPTILE_1610_CUTOUT_IMAGE',
        groups: ['E3851A', 'E3851B'],
        defaultGroup: 'E3851A',
        featureOnGroup: 'E3851B',
    },
    ARRANGEMENT_LIST_ELEMENTS_ON_TOP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ARRANGEMENT_LIST_ELEMENTS_ON_TOP,
        experimentToggle: 'REPTILE_1288_ARRANGEMENT_EXPERIMENT',
        featureToggle: 'REPTILE_1288_ARRANGEMENT',
        groups: ['StatusQuo', 'Positions'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Positions',
    },
    CLEAN_TILE_V2: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.CLEAN_TILE_V2,
        experimentToggle: 'REPTILE_1614_CLEAN_TILE_V2_EXPERIMENT',
        featureToggle: 'REPTILE_1614_CLEAN_TILE_V2',
        groups: ['E3867A', 'E3867B'],
        defaultGroup: 'E3867A',
        featureOnGroup: 'E3867B',
    },
} as const satisfies ExperimentConfigs;
